import type { ErrorSchema } from '@rjsf/utils';
import type { LinkRow, LinkRowErrors, MenuItem, MenuItemErrors } from './MenuBuilderCore';

const URL_INVALID_MESSAGE = 'Enter a full URL, e.g. https://example.com';
const URL_REQUIRED_MESSAGE = 'URL is required';
const ITEM_INVALID_MESSAGE = 'This item contains invalid data';

interface ErrorNode {
  __errors?: string[];
  [field: string]: ErrorNode | string[] | undefined;
}

const childNode = (node: ErrorNode | undefined, key: string): ErrorNode | undefined => {
  const child = node?.[key];
  return Array.isArray(child) ? undefined : child;
};

const hasDeepErrors = (node: ErrorNode | undefined): boolean => {
  if (!node) return false;
  if (node.__errors?.length) return true;
  return Object.keys(node).some((key) => key !== '__errors' && hasDeepErrors(childNode(node, key)));
};

// The oneOf link/group schema makes AJV report errors from both branches plus
// "must match exactly one schema in oneOf" noise. Instead of surfacing those raw
// messages, derive friendly ones from the fields the user can actually edit.
const getRowErrors = (row: LinkRow, node: ErrorNode | undefined): LinkRowErrors => {
  if (!node || row.isReadOnly) return {};
  const result: LinkRowErrors = {};
  const labelErrors = childNode(node, 'label')?.__errors;
  if (labelErrors?.length) result.label = labelErrors[0];
  if (childNode(node, 'url')?.__errors?.length) {
    result.url = row.url?.trim() ? URL_INVALID_MESSAGE : URL_REQUIRED_MESSAGE;
  }
  return result;
};

const getItemErrors = (item: MenuItem, node: ErrorNode | undefined): MenuItemErrors | undefined => {
  if (!node) return undefined;
  const result: MenuItemErrors = {};

  if (item.kind === 'link') {
    Object.assign(result, getRowErrors(item, node));
  } else {
    const labelErrors = childNode(node, 'label')?.__errors;
    if (labelErrors?.length) result.label = labelErrors[0];
    const linksNode = childNode(node, 'links');
    const linkErrors: Record<number, LinkRowErrors> = {};
    item.links.forEach((link, li) => {
      const rowErrors = getRowErrors(link, childNode(linksNode, String(li)));
      if (rowErrors.label || rowErrors.url) linkErrors[li] = rowErrors;
    });
    if (Object.keys(linkErrors).length > 0) result.links = linkErrors;
  }

  if (!result.label && !result.url && !result.links && hasDeepErrors(node)) {
    result.item = ITEM_INVALID_MESSAGE;
  }

  if (!result.label && !result.url && !result.links && !result.item) return undefined;
  return result;
};

export function getMenuItemErrors(
  items: MenuItem[],
  errorSchema: ErrorSchema<MenuItem[]> | undefined
): Record<number, MenuItemErrors> {
  const result: Record<number, MenuItemErrors> = {};
  if (!errorSchema) return result;

  const root = errorSchema as unknown as ErrorNode;
  items.forEach((item, i) => {
    const itemErrors = getItemErrors(item, childNode(root, String(i)));
    if (itemErrors) result[i] = itemErrors;
  });
  return result;
}
